/**
 * Lightweight Markdown -> HTML renderer used by CMS/public content.
 * If content already looks like HTML, keep it as-is for backward compatibility.
 */

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderInline(text: string): string {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*(?!\s)(.+?)(?<!\s)\*(?=$|[\s).,!?:;])/g, '$1<em>$2</em>')
    .replace(/(^|[\s(])_(?!\s)(.+?)(?<!\s)_(?=$|[\s).,!?:;])/g, '$1<em>$2</em>');
}

function closeList(html: string[], listType: 'ul' | 'ol' | null): null {
  if (listType) {
    html.push(`</${listType}>`);
  }
  return null;
}

export function markdownToHtml(markdown: string): string {
  if (!markdown) return '';

  const source = markdown
    .replace(/^\uFEFF/, '')
    .replace(/\\n/g, '\n')
    .trim();
  if (!source) return '';

  if (HTML_TAG_RE.test(source)) {
    return source;
  }

  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let inCodeBlock = false;
  let codeFence: string[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      listType = closeList(html, listType);
      if (inCodeBlock) {
        html.push(`<pre><code>${escapeHtml(codeFence.join('\n'))}</code></pre>`);
        codeFence = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeFence.push(rawLine);
      continue;
    }

    if (!trimmed) {
      listType = closeList(html, listType);
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      listType = closeList(html, listType);
      const level = headingMatch[1].length;
      html.push(`<h${level}>${renderInline(headingMatch[2])}</h${level}>`);
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      listType = closeList(html, listType);
      html.push(`<blockquote>${renderInline(trimmed.replace(/^>\s?/, ''))}</blockquote>`);
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      if (listType !== 'ul') {
        listType = closeList(html, listType);
        html.push('<ul>');
        listType = 'ul';
      }
      html.push(`<li>${renderInline(trimmed.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      if (listType !== 'ol') {
        listType = closeList(html, listType);
        html.push('<ol>');
        listType = 'ol';
      }
      html.push(`<li>${renderInline(orderedMatch[1])}</li>`);
      continue;
    }

    listType = closeList(html, listType);
    html.push(`<p>${renderInline(trimmed)}</p>`);
  }

  if (inCodeBlock) {
    html.push(`<pre><code>${escapeHtml(codeFence.join('\n'))}</code></pre>`);
  }

  closeList(html, listType);

  return html.join('\n');
}
